import { Fragment } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useCallback, useRef } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Shield,
  RefreshCw,
  Euro,
  Send,
  Upload,
  CheckCircle2,
  FileText,
  Archive,
  ArchiveRestore,
  Trash2,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useAdmin } from "@/hooks/use-admin";
import { storePostLoginRedirect } from "@/lib/auth-redirect";
import {
  listAllConciergeRequests,
  updateConciergeStatus,
  setConciergePrice,
  createConciergeCheckout,
  saveConciergeDeliverable,
  createDeliverableUploadUrl,
  deliverConcierge,
  setConciergeArchived,
  deleteConciergeRequest,
  listConciergeAgents,
  assignConciergeRequest,
  reconcileConciergePayment,
} from "@/lib/concierge.functions";
import {
  getConciergeCommercialConfig,
  listConciergeQuotes,
} from "@/lib/concierge-quotes.functions";
import { taxBehaviorLabel } from "@/lib/concierge-quote";
import {
  ALLOWED_STATUS_TRANSITIONS,
  PAYMENT_STATE_LABEL,
  REQUEST_STATE_LABEL,
  availableActions,
  derivePaymentState,
  deriveRequestState,
  type ConciergeSnapshot,
  type DbStatus,
} from "@/lib/concierge-workflow";

export const Route = createFileRoute("/concierge/admin")({ component: AdminConciergePage });

const STATUSES = [
  "novo",
  "em_analise",
  "em_desenvolvimento",
  "concluido",
  "entregue",
  "cancelado",
] as const;

const LABEL: Record<string, string> = {
  novo: "Novo",
  em_analise: "Em análise",
  em_desenvolvimento: "Em desenvolvimento",
  concluido: "Concluído",
  entregue: "Entregue",
  cancelado: "Cancelado",
};

const PAY_LABEL: Record<string, string> = {
  nao_aplicavel: "—",
  pendente: "A aguardar pagamento",
  pago: "Pago",
};

type Row = {
  id: string;
  destination: string;
  start_date: string | null;
  end_date: string | null;
  people: number | null;
  budget: number | null;
  interests: string[] | null;
  transport: string | null;
  accommodation: string | null;
  notes: string | null;
  status: string;
  created_at: string;
  user_id: string;
  requester_email: string | null;
  requester_name: string | null;
  price_cents: number | null;
  payment_status: string;
  checkout_url: string | null;
  deliverable_text: string | null;
  deliverable_pdf_url: string | null;
  delivered_at: string | null;
  is_first_free: boolean;
  app_route_link: string | null;
  archived_at: string | null;
  assigned_to: string | null;
  active_quote: { status: string; expiresAt: string | null } | null;
};

/** Snapshot para a camada de política (estado do pedido ≠ estado do pagamento). */
function snapshotOf(r: Row): ConciergeSnapshot {
  return {
    status: r.status,
    paymentStatus: r.payment_status ?? "",
    isFirstFree: r.is_first_free,
    priceCents: r.price_cents,
    deliveredAt: r.delivered_at,
    hasDeliverable: Boolean(r.deliverable_text || r.deliverable_pdf_url || r.app_route_link),
    activeQuote: r.active_quote ?? null,
  };
}

const PAYMENT_BADGE: Record<string, string> = {
  pago: "bg-emerald-100 text-emerald-800",
  gratuito: "bg-emerald-100 text-emerald-800",
  checkout_enviado: "bg-amber-100 text-amber-800",
  em_processamento: "bg-amber-100 text-amber-800",
  checkout_por_emitir: "bg-slate-100 text-slate-700",
  expirado: "bg-orange-100 text-orange-800",
  falhado: "bg-red-100 text-red-800",
  em_revisao: "bg-red-100 text-red-800",
  reembolsado: "bg-slate-200 text-slate-700",
};

const REQUEST_BADGE: Record<string, string> = {
  entregue: "bg-emerald-100 text-emerald-800",
  cancelado: "bg-slate-200 text-slate-600",
  pronto: "bg-sky-100 text-sky-800",
  em_desenvolvimento: "bg-indigo-100 text-indigo-800",
  pago: "bg-emerald-100 text-emerald-800",
  aguarda_pagamento: "bg-amber-100 text-amber-800",
  orcamento_enviado: "bg-amber-100 text-amber-800",
  em_analise: "bg-blue-100 text-blue-800",
  novo: "bg-slate-100 text-slate-700",
};

function Badge({ tone, children }: { tone: string; children: React.ReactNode }) {
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>
      {children}
    </span>
  );
}

type QuoteRow = {
  id: string;
  version: number;
  amount_cents: number;
  currency: string;
  environment: string;
  tax_behavior: string;
  status: string;
  checkout_session_id: string | null;
  expires_at: string | null;
  mismatch_reason: string | null;
};

const QUOTE_STATUS_LABEL: Record<string, string> = {
  draft: "Rascunho",
  quoted: "Orçamentado",
  checkout_created: "Link ativo",
  paid: "Pago",
  expired: "Expirado",
  superseded: "Substituído",
  cancelled: "Cancelado",
  payment_mismatch: "Divergência de pagamento",
};

type AgentOption = { id: string; email: string; displayName: string | null };

function AdminConciergePage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading } = useAdmin();
  const navigate = useNavigate();
  const listFn = useServerFn(listAllConciergeRequests);
  const updateFn = useServerFn(updateConciergeStatus);
  const setPriceFn = useServerFn(setConciergePrice);
  const checkoutFn = useServerFn(createConciergeCheckout);
  const saveDelivFn = useServerFn(saveConciergeDeliverable);
  const uploadUrlFn = useServerFn(createDeliverableUploadUrl);
  const deliverFn = useServerFn(deliverConcierge);
  const archiveFn = useServerFn(setConciergeArchived);
  const deleteFn = useServerFn(deleteConciergeRequest);
  const agentsFn = useServerFn(listConciergeAgents);
  const assignFn = useServerFn(assignConciergeRequest);
  const reconcileFn = useServerFn(reconcileConciergePayment);

  const [rows, setRows] = useState<Row[]>([]);
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      storePostLoginRedirect();
      navigate({ to: "/login" });
    }
  }, [authLoading, isAuthenticated, navigate]);

  const refresh = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const r = await listFn({ data: { includeArchived: showArchived } });
      setRows(r as unknown as Row[]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro");
    } finally {
      setLoading(false);
    }
  }, [isAdmin, listFn, showArchived]);

  useEffect(() => {
    if (isAdmin) refresh();
  }, [isAdmin, refresh]);

  useEffect(() => {
    if (!isAdmin) return;
    agentsFn()
      .then((r) => setAgents(r.agents))
      .catch(() => setAgents([]));
  }, [isAdmin, agentsFn]);

  async function changeAssignment(id: string, agentId: string | null) {
    try {
      await assignFn({ data: { id, agentId } });
      toast.success(agentId ? "Pedido atribuído" : "Atribuição removida");
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro");
    }
  }

  async function toggleArchive(id: string, archived: boolean) {
    try {
      await archiveFn({ data: { id, archived } });
      toast.success(archived ? "Pedido arquivado" : "Pedido restaurado");
      refresh();
      if (expanded === id) setExpanded(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro");
    }
  }

  async function hardDelete(id: string) {
    if (!confirm("Apagar este pedido definitivamente? Esta ação não pode ser revertida.")) return;
    try {
      await deleteFn({ data: { id } });
      toast.success("Pedido apagado");
      if (expanded === id) setExpanded(null);
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro");
    }
  }

  if (authLoading || roleLoading) {
    return (
      <main className="mx-auto flex min-h-[60vh] w-full max-w-6xl items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="mx-auto min-h-[60vh] w-full max-w-2xl px-4 py-16 text-center">
        <Shield className="mx-auto h-12 w-12 text-muted-foreground" />
        <h1 className="mt-4 text-2xl font-bold">Acesso restrito</h1>
      </main>
    );
  }

  async function changeStatus(id: string, status: string, override = false) {
    try {
      await updateFn({
        data: { id, status: status as (typeof STATUSES)[number], note: "", override },
      });
      toast.success("Estado atualizado");
      refresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Erro";
      // Trabalhar antes do pagamento exige um override explícito e auditado.
      if (!override && msg.includes("override auditado")) {
        if (
          window.confirm(
            "Este pedido ainda não está pago. Queres iniciar o desenvolvimento mesmo assim? A decisão fica registada no histórico.",
          )
        ) {
          return changeStatus(id, status, true);
        }
        return;
      }
      toast.error(msg);
    }
  }

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-8">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Admin
      </Link>

      <header className="mt-4 mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Pedidos de Planeamento</h1>
          <p className="text-sm text-muted-foreground">
            {showArchived ? `${rows.length} arquivados` : `${rows.length} ativos`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-input bg-background p-1">
            <button
              onClick={() => setShowArchived(false)}
              className={`rounded-full px-3 py-1 text-sm transition ${!showArchived ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}
            >
              Ativos
            </button>
            <button
              onClick={() => setShowArchived(true)}
              className={`rounded-full px-3 py-1 text-sm transition ${showArchived ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}
            >
              Arquivados
            </button>
          </div>
          <button
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-1 rounded-full border border-input bg-background px-3 py-1.5 text-sm hover:bg-accent"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Atualizar
          </button>
        </div>
      </header>

      <div className="overflow-x-auto rounded-2xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase">
            <tr>
              <th className="p-3">Cliente</th>
              <th className="p-3">Destino</th>
              <th className="p-3">Datas</th>
              <th className="p-3">Pax</th>
              <th className="p-3">Pagamento</th>
              <th className="p-3">Estado</th>
              <th className="p-3">Colaborador</th>
              <th className="p-3">Criado</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Fragment key={r.id}>
                <tr className="border-t border-border">
                  <td className="p-3">
                    <div className="font-medium">{r.requester_name || "—"}</div>
                    {r.requester_email && (
                      <a
                        href={`mailto:${r.requester_email}`}
                        className="text-xs text-brand hover:underline"
                      >
                        {r.requester_email}
                      </a>
                    )}
                  </td>
                  <td className="p-3 font-medium">{r.destination}</td>
                  <td className="p-3">
                    {r.start_date || "—"} → {r.end_date || "—"}
                  </td>
                  <td className="p-3">{r.people ?? "—"}</td>
                  <td className="p-3 text-xs">
                    <div className="font-semibold">
                      {r.is_first_free
                        ? "Grátis (1º)"
                        : r.price_cents
                          ? `€ ${(r.price_cents / 100).toFixed(2)}`
                          : "—"}
                    </div>
                    <div className="mt-1">
                      <Badge tone={PAYMENT_BADGE[derivePaymentState(snapshotOf(r))] ?? "bg-slate-100 text-slate-700"}>
                        {PAYMENT_STATE_LABEL[derivePaymentState(snapshotOf(r))]}
                      </Badge>
                    </div>
                  </td>
                  <td className="p-3">
                    <Badge tone={REQUEST_BADGE[deriveRequestState(snapshotOf(r))] ?? "bg-slate-100 text-slate-700"}>
                      {REQUEST_STATE_LABEL[deriveRequestState(snapshotOf(r))]}
                    </Badge>
                    <select
                      value=""
                      onChange={(e) => {
                        if (e.target.value) changeStatus(r.id, e.target.value);
                      }}
                      className="mt-1 block rounded-md border border-input bg-background px-2 py-1 text-[11px]"
                    >
                      <option value="">Mudar estado…</option>
                      {(ALLOWED_STATUS_TRANSITIONS[r.status as DbStatus] ?? [])
                        .filter((s) => s !== "entregue")
                        .map((s) => (
                          <option key={s} value={s}>
                            {LABEL[s]}
                          </option>
                        ))}
                    </select>
                  </td>
                  <td className="p-3">
                    <select
                      value={r.assigned_to ?? ""}
                      onChange={(e) => changeAssignment(r.id, e.target.value || null)}
                      className="max-w-[160px] rounded-md border border-input bg-background px-2 py-1 text-xs"
                    >
                      <option value="">— não atribuído —</option>
                      {agents.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.displayName || a.email || a.id.slice(0, 6)}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td className="p-3 text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                        className="text-xs text-brand hover:underline"
                      >
                        {expanded === r.id ? "Fechar" : "Gerir"}
                      </button>
                      <button
                        onClick={() => toggleArchive(r.id, !r.archived_at)}
                        title={r.archived_at ? "Restaurar pedido" : "Arquivar pedido"}
                        className="rounded-full p-1.5 text-muted-foreground transition hover:bg-accent hover:text-foreground"
                      >
                        {r.archived_at ? (
                          <ArchiveRestore className="h-3.5 w-3.5" />
                        ) : (
                          <Archive className="h-3.5 w-3.5" />
                        )}
                      </button>
                      {r.archived_at && (
                        <button
                          onClick={() => hardDelete(r.id)}
                          title="Apagar definitivamente"
                          className="rounded-full p-1.5 text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {expanded === r.id && (
                  <tr className="bg-muted/20">
                    <td colSpan={9} className="p-4 text-sm">
                      <div className="grid grid-cols-2 gap-3">
                        <Info label="Transporte" value={r.transport} />
                        <Info label="Alojamento" value={r.accommodation} />
                        <Info label="Interesses" value={(r.interests ?? []).join(", ")} />
                        <Info label="Orçamento" value={r.budget ? `€ ${r.budget}` : null} />
                      </div>
                      {r.notes && (
                        <div className="mt-3">
                          <div className="text-xs font-semibold uppercase text-muted-foreground">
                            Notas do cliente
                          </div>
                          <div className="whitespace-pre-wrap">{r.notes}</div>
                        </div>
                      )}
                      <hr className="my-4 border-border" />
                      <ManageBlock
                        row={r}
                        onSaved={refresh}
                        setPriceFn={setPriceFn}
                        checkoutFn={checkoutFn}
                        saveDelivFn={saveDelivFn}
                        uploadUrlFn={uploadUrlFn}
                        deliverFn={deliverFn}
                        reconcileFn={reconcileFn}
                        onStatus={changeStatus}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={9} className="p-8 text-center text-muted-foreground">
                  Sem pedidos ainda.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </main>
  );
}

function Info({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase text-muted-foreground">{label}</div>
      <div>{value || "—"}</div>
    </div>
  );
}

type FnBag = {
  row: Row;
  onSaved: () => void;
  setPriceFn: ReturnType<typeof useServerFn<typeof setConciergePrice>>;
  checkoutFn: ReturnType<typeof useServerFn<typeof createConciergeCheckout>>;
  saveDelivFn: ReturnType<typeof useServerFn<typeof saveConciergeDeliverable>>;
  uploadUrlFn: ReturnType<typeof useServerFn<typeof createDeliverableUploadUrl>>;
  deliverFn: ReturnType<typeof useServerFn<typeof deliverConcierge>>;
  reconcileFn: ReturnType<typeof useServerFn<typeof reconcileConciergePayment>>;
  onStatus: (id: string, status: string) => void | Promise<void>;
};

function ManageBlock({
  row,
  onSaved,
  setPriceFn,
  checkoutFn,
  saveDelivFn,
  uploadUrlFn,
  deliverFn,
  reconcileFn,
  onStatus,
}: FnBag) {
  const [priceEur, setPriceEur] = useState(
    row.price_cents ? (row.price_cents / 100).toString() : "",
  );
  const [isFree, setIsFree] = useState(row.is_first_free);
  const [text, setText] = useState(row.deliverable_text ?? "");
  const [pdfPath, setPdfPath] = useState(row.deliverable_pdf_url ?? "");
  const [appRouteLink, setAppRouteLink] = useState(row.app_route_link ?? "");
  const [busy, setBusy] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const quotesFn = useServerFn(listConciergeQuotes);
  const configFn = useServerFn(getConciergeCommercialConfig);
  const [quotes, setQuotes] = useState<QuoteRow[]>([]);
  const [fiscal, setFiscal] = useState<{
    environment: string;
    taxBehavior: string | null;
    errors: string[];
  } | null>(null);

  const loadQuotes = useCallback(async () => {
    try {
      const [q, cfg] = await Promise.all([quotesFn({ data: { requestId: row.id } }), configFn()]);
      setQuotes(q as unknown as QuoteRow[]);
      const env = cfg.activeEnvironment as "sandbox" | "live";
      const scoped = env === "live" ? cfg.live : cfg.sandbox;
      setFiscal({
        environment: env,
        taxBehavior: scoped.config.taxBehavior,
        errors: scoped.errors,
      });
    } catch {
      /* painel continua utilizável */
    }
  }, [quotesFn, configFn, row.id]);

  useEffect(() => {
    loadQuotes();
  }, [loadQuotes]);

  const activeQuote =
    quotes.find((q) => q.status === "quoted" || q.status === "checkout_created") ?? null;
  const fiscalBlocked = (fiscal?.errors.length ?? 0) > 0;

  async function savePrice() {
    setBusy("price");
    try {
      const cents = priceEur ? Math.round(Number(priceEur) * 100) : null;
      await setPriceFn({ data: { id: row.id, priceCents: cents, isFirstFree: isFree } });
      toast.success("Preço/estado atualizado");
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro");
    } finally {
      setBusy(null);
    }
  }
  async function sendPay() {
    if (fiscalBlocked) {
      toast.error(
        "Configuração fiscal Concierge incompleta — resolve em Operações antes de emitir.",
      );
      return;
    }
    if (
      activeQuote &&
      !window.confirm(
        `Já existe o orçamento v${activeQuote.version} enviado (€ ${(activeQuote.amount_cents / 100).toFixed(2)}). ` +
          "Substituir cancela a sessão de pagamento anterior e cria uma nova versão. Continuar?",
      )
    )
      return;
    setBusy("pay");
    try {
      await checkoutFn({ data: { id: row.id, confirmSupersede: Boolean(activeQuote) } });
      toast.success("Link de pagamento enviado ao cliente por email");
      await loadQuotes();
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro");
    } finally {
      setBusy(null);
    }
  }
  async function saveDeliverable() {
    setBusy("deliv");
    try {
      await saveDelivFn({
        data: {
          id: row.id,
          text: text || null,
          pdfPath: pdfPath || null,
          appRouteLink: appRouteLink || null,
        },
      });
      toast.success("Conteúdo da rota guardado");
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro");
    } finally {
      setBusy(null);
    }
  }
  async function uploadPdf(file: File) {
    setBusy("upload");
    try {
      const { path, signedUrl } = await uploadUrlFn({ data: { id: row.id, filename: file.name } });
      const put = await fetch(signedUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/pdf" },
      });
      if (!put.ok) throw new Error("Upload falhou");
      setPdfPath(path);
      toast.success("PDF carregado. Não te esqueças de Guardar.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro");
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  }
  async function deliver() {
    setBusy("deliver");
    try {
      await deliverFn({ data: { id: row.id } });
      toast.success("Entregue ao cliente");
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro");
    } finally {
      setBusy(null);
    }
  }
  async function reconcile() {
    const reason = window.prompt(
      "Motivo da reconciliação manual (fica registado no histórico):",
      "Pagamento confirmado no Stripe mas o webhook falhou",
    );
    if (!reason || reason.trim().length < 5) return;
    setBusy("reconcile");
    try {
      await reconcileFn({ data: { id: row.id, reason: reason.trim() } });
      toast.success("Pagamento reconciliado");
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro");
    } finally {
      setBusy(null);
    }
  }

  const snap: ConciergeSnapshot = {
    ...snapshotOf(row),
    activeQuote: activeQuote
      ? { status: activeQuote.status, expiresAt: activeQuote.expires_at }
      : (row.active_quote ?? null),
  };
  const actions = availableActions(snap, { isFounder: true });
  const can = (a: (typeof actions)[number]) => actions.includes(a);

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-bold uppercase text-muted-foreground">Ações</span>
        {can("start_development") && (
          <ActionButton
            disabled={busy !== null}
            onClick={() => onStatus(row.id, "em_desenvolvimento")}
          >
            Iniciar desenvolvimento
          </ActionButton>
        )}
        {can("mark_ready") && (
          <ActionButton disabled={busy !== null} onClick={() => onStatus(row.id, "concluido")}>
            Marcar como pronto
          </ActionButton>
        )}
        {can("deliver") && (
          <ActionButton disabled={busy !== null} tone="primary" onClick={deliver}>
            Entregar ao cliente
          </ActionButton>
        )}
        {can("reconcile_payment") && (
          <ActionButton disabled={busy !== null} onClick={reconcile}>
            {busy === "reconcile" ? "A reconciliar…" : "Reconciliar pagamento"}
          </ActionButton>
        )}
        {can("cancel") && (
          <ActionButton
            disabled={busy !== null}
            tone="danger"
            onClick={() => {
              if (window.confirm("Cancelar este pedido?")) onStatus(row.id, "cancelado");
            }}
          >
            Cancelar pedido
          </ActionButton>
        )}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
      <div className="rounded-xl border border-border bg-background p-4">
        <div className="mb-2 text-xs font-bold uppercase text-muted-foreground">Pagamento</div>
        <label className="mb-2 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={isFree} onChange={(e) => setIsFree(e.target.checked)} />
          1º pedido grátis desta pessoa
        </label>
        <div className="flex items-center gap-2">
          <Euro className="h-4 w-4 text-muted-foreground" />
          <input
            type="number"
            min={0}
            step="0.01"
            disabled={isFree}
            value={priceEur}
            onChange={(e) => setPriceEur(e.target.value)}
            placeholder="Preço €"
            className="w-28 rounded-md border border-input bg-background px-2 py-1 text-sm disabled:opacity-50"
          />
          <button
            onClick={savePrice}
            disabled={busy !== null}
            className="rounded-full border border-input px-3 py-1 text-xs hover:bg-accent"
          >
            {busy === "price" ? <Loader2 className="h-3 w-3 animate-spin" /> : "Guardar"}
          </button>
        </div>
        <button
          onClick={sendPay}
          disabled={busy !== null || isFree || !row.price_cents}
          className="mt-3 inline-flex items-center gap-1 rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
        >
          {busy === "pay" ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Send className="h-3 w-3" />
          )}
          Enviar link de pagamento
        </button>
        {row.checkout_url && (
          <p className="mt-2 text-xs text-muted-foreground">
            Link já enviado ao cliente por email.
          </p>
        )}
        {fiscal && (
          <p className="mt-2 text-xs text-muted-foreground">
            Ambiente <strong>{fiscal.environment}</strong> ·{" "}
            {taxBehaviorLabel(fiscal.taxBehavior as never)}
          </p>
        )}
        {fiscalBlocked && (
          <p className="mt-1 text-xs font-semibold text-destructive">
            Não é possível emitir checkout: {fiscal?.errors.join(" ")}
          </p>
        )}
        {quotes.length > 0 && (
          <div className="mt-3 border-t border-border pt-2">
            <div className="mb-1 text-[11px] font-bold uppercase text-muted-foreground">
              Histórico de orçamentos
            </div>
            <ul className="space-y-1 text-xs">
              {quotes.map((q) => (
                <li
                  key={q.id}
                  className="flex flex-wrap items-center gap-x-2 text-muted-foreground"
                >
                  <span className="font-semibold text-foreground">v{q.version}</span>
                  <span>
                    € {(q.amount_cents / 100).toFixed(2)} {q.currency.toUpperCase()}
                  </span>
                  <span>· {q.tax_behavior === "inclusive" ? "IVA incluído" : "IVA acrescido"}</span>
                  <span>· {q.environment}</span>
                  <span>· {QUOTE_STATUS_LABEL[q.status] ?? q.status}</span>
                  {q.expires_at && (
                    <span>· válido até {new Date(q.expires_at).toLocaleString("pt-PT")}</span>
                  )}
                  {q.checkout_session_id && (
                    <span className="font-mono">· {q.checkout_session_id.slice(0, 14)}…</span>
                  )}
                  {q.mismatch_reason && (
                    <span className="font-semibold text-destructive">· {q.mismatch_reason}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-background p-4">
        <div className="mb-2 text-xs font-bold uppercase text-muted-foreground">Rota final</div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          placeholder="Escreve a rota (aceita markdown simples). Podes deixar em branco se anexares só um PDF."
          className="w-full rounded-md border border-input bg-background p-2 text-sm"
        />
        <div className="mt-3">
          <div className="text-xs font-semibold text-muted-foreground">Link da rota na app</div>
          <input
            type="text"
            value={appRouteLink}
            onChange={(e) => setAppRouteLink(e.target.value)}
            placeholder="https://routify-tour.com/pro/r/..."
            className="mt-1 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
          />
        </div>
        <div className="mt-2 flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadPdf(f);
            }}
            className="text-xs"
          />
          {busy === "upload" && <Loader2 className="h-3 w-3 animate-spin" />}
        </div>
        {pdfPath && (
          <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
            <FileText className="h-3 w-3" /> {pdfPath.split("/").pop()}
          </div>
        )}
        <div className="mt-3 flex gap-2">
          <button
            onClick={saveDeliverable}
            disabled={busy !== null}
            className="rounded-full border border-input px-3 py-1.5 text-xs hover:bg-accent"
          >
            {busy === "deliv" ? <Loader2 className="h-3 w-3 animate-spin" /> : "Guardar rascunho"}
          </button>
          <button
            onClick={deliver}
            disabled={busy !== null || !can("deliver")}
            className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
          >
            {busy === "deliver" ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <CheckCircle2 className="h-3 w-3" />
            )}
            {row.delivered_at ? "Entregue" : "Entregar ao cliente"}
          </button>
        </div>
        {!can("deliver") && !row.delivered_at && (
          <p className="mt-2 text-[11px] text-amber-600">
            Para entregar: pagamento confirmado (ou 1º grátis), estado “Pronto para entrega” e
            conteúdo guardado.
          </p>
        )}
      </div>
      </div>
    </>
  );
}

function ActionButton({
  children,
  onClick,
  disabled,
  tone = "default",
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone?: "default" | "primary" | "danger";
}) {
  const cls =
    tone === "primary"
      ? "bg-emerald-600 text-white hover:bg-emerald-700"
      : tone === "danger"
        ? "border border-destructive/40 text-destructive hover:bg-destructive/10"
        : "border border-input hover:bg-accent";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`rounded-full px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${cls}`}
    >
      {children}
    </button>
  );
}
